import React, {
	useMemo,
	useState,
} from 'react';
import {
	Checkbox,
	Dropdown,
	Menu,
	Space,
} from 'antd';
import { useQuery } from 'react-query';
import {
	AccountingItemApprovalStates,
	CargoContractSides,
	FixtureCounterpartyTypes,
} from '@shared/utils/constants';
import type { GetCounterpartiesResponse } from '@api/features/counterparties/getCounterparties';
import AddButton from '@client/components/AddButton';
import {
	getCounterparties,
	getVoyageInvoices,
	submitHireInvoice,
} from '@client/lib/api';
import useFetchedState from '@client/utils/hooks/useFetchedState';
import Table from '@client/components/Table/Table';
import {
	onDeleteHireInvoice,
	onUnsubmitHireInvoice,
} from '@client/screens/fleet/VoyageDetailsScreen/components/InvoiceTables/helpers/invoiceFunctions';
import { getFreightInvoiceColumns } from '@client/screens/fleet/VoyageDetailsScreen/helpers/tableColumns';
import Card from '@client/components/Card/Card';
import showSuccessNotification from '@client/utils/showSuccessNotification';
import useLocalStorage from '@client/utils/hooks/useLocalStorage';
import { useVoyage } from '../VoyageProvider/VoyageProvider';
import FreightInvoiceForm from './FreightInvoiceForm';
import styles from './HireInvoicesTable.module.css';

type Props = {
	cargoContractSide?: CargoContractSides;
};

const FreightInvoicesTable = ({
	cargoContractSide,
}: Props) => {
	const {
		voyageDetails,
		refreshDetails,
		voyageInvoices,
		cargos,
	} = useVoyage();

	const {
		data: allInvoicesIncludingDeleted,
		isFetching: allInvoicesLoading,
		refetch: refetchAllInvoices,
	} = useQuery(
		['voyageInvoicesWithDeleted', voyageDetails?.id],
		() => getVoyageInvoices(voyageDetails!.id, { includeDeleted: true }),
		{
			enabled: !!voyageDetails?.id,
			refetchOnWindowFocus: false,
		},
	);

	const invoices = useMemo(() => {
		const source = allInvoicesIncludingDeleted ?? voyageInvoices ?? [];

		if (cargoContractSide === CargoContractSides.VC_IN) {
			return source.filter(invoice => invoice.isCargoRelet);
		}

		if (cargoContractSide === CargoContractSides.VC_OUT) {
			return source.filter(invoice => !invoice.isCargoRelet);
		}

		return source;
	}, [allInvoicesIncludingDeleted, voyageInvoices, cargoContractSide]);

	const [charterers, _reloadCharterers, _error, _loading] = useFetchedState(
		() => getCounterparties(FixtureCounterpartyTypes.CHARTEREROWNER),
	);

	const [hideDeleted, setHideDeleted] = useLocalStorage('hideDeletedInvoices', true);

	const [freightInvoiceFormOpen, setFreightInvoiceFormOpen] = useState(false);
	const [selectedCharterer, setSelectedCharterer] = useState<
		GetCounterpartiesResponse[number] | null>(null);

	const expandedRowKeys = useMemo(() => {
		return invoices.reduce<Array<number>>((acc, h) => {
			if (h.state === AccountingItemApprovalStates.REJECTED) {
				acc.push(h.id);
			}

			return acc;
		}, []);
	}, [invoices]);

	const refreshAll = () => Promise.all([refreshDetails(), refetchAllInvoices()]);

	const freightInvoiceColumns = getFreightInvoiceColumns({
		voyageDetails,
		charterers,
		voyageInvoices: invoices,
		onDelete: async (id) => {
			await onDeleteHireInvoice(id);
			await refreshAll();
		},
		onSubmit: async (id) => {
			if (voyageInvoices != null) {
				const requiresApproval = voyageInvoices[0]?.requiresApproval;
				const successMsg = requiresApproval ? 'Submitted for approval' : 'Invoice approved';
				await submitHireInvoice(id);
				showSuccessNotification(successMsg);
				await refreshAll();
			}
		},
		onUndo: async (id) => {
			await onUnsubmitHireInvoice(id);
			await refreshAll();
		},
	});

	const cargoCharterers = charterers?.filter((c) => (
		cargos?.find((cargo) => cargo.charterer === c.id) != null
	));

	const isCargoRelet = cargoContractSide != null;

	const selectedCargo = useMemo(() => {
		if (cargoContractSide == null) {
			return null;
		}

		return cargos?.find(cargo => cargo.contractSide === cargoContractSide) ?? null;
	}, [cargos, cargoContractSide]);

	const selectedCargoCharterer = useMemo(() => {
		if (selectedCargo?.charterer == null) {
			return null;
		}

		return charterers?.find(charterer => charterer.id === selectedCargo.charterer) ?? null;
	}, [charterers, selectedCargo]);

	const hasDeletedInvoices = invoices.some((i) => i.deletedAt != null);
	const visibleInvoices = hideDeleted ?
		invoices.filter((i) => i.deletedAt == null) :
		invoices;

	const hideDeletedCheckbox = hasDeletedInvoices ? (
		<Checkbox
			checked={hideDeleted}
			onChange={(e) => setHideDeleted(e.target.checked)}
		>
			Hide deleted
		</Checkbox>
	) : null;

	return (
		<Card
			slim
			title="Invoices"
			extra={isCargoRelet ? (
				<Space>
					{hideDeletedCheckbox}
					<AddButton
						data-tour="createHireInvoice"
						loading={allInvoicesLoading}
						onClick={() => {
							setSelectedCharterer(selectedCargoCharterer);
							setFreightInvoiceFormOpen(true);
						}}
						disabled={selectedCargoCharterer == null}
					>
						New Invoice
					</AddButton>
				</Space>
			) : (
				<Space>
					{hideDeletedCheckbox}
					<Dropdown
						trigger={['click']}
						overlay={(
							<Menu>
								{cargoCharterers != null && cargoCharterers.map((charterer) => (
									<Menu.Item
										key={charterer.name}
										onClick={() => {
											setSelectedCharterer(charterer);
											setFreightInvoiceFormOpen(true);
										}}
									>
										{charterer.name}
									</Menu.Item>
								))}
							</Menu>
						)}
					>
						<AddButton
							data-tour="createHireInvoice"
							loading={allInvoicesLoading}
						>
							New Invoice
						</AddButton>
					</Dropdown>
				</Space>
			)}
		>
			<Table
				data-tour="freightInvoicesTable"
				size="middle"
				rowKey="id"
				// @ts-ignore
				columns={freightInvoiceColumns}
				rowClassName={(row) => row.deletedAt != null ? styles.deletedInvoiceRow : ''}
				dataSource={visibleInvoices}
				loading={allInvoicesLoading}
				expandable={{
					expandedRowRender: (row) => (row.stateNote != null && (
						<div>
							<strong>Reason:</strong>
							<p>{row.stateNote}</p>
						</div>
					)),
					rowExpandable: (row) => row.stateNote != null,
					defaultExpandedRowKeys: expandedRowKeys,
				}}
			/>
			<FreightInvoiceForm
				open={freightInvoiceFormOpen}
				onClose={() => setFreightInvoiceFormOpen(false)}
				onInvoiceCreated={refreshAll}
				selectedCharterer={selectedCharterer}
				cargoContractSide={cargoContractSide}
			/>
		</Card>
	);
};

export default FreightInvoicesTable;
